import { MAX_VIDEO_BYTES, MAX_VIDEO_TITLE, videoTypeFor, type VideoDto } from '@process-hub/shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { describeError } from '../errors';
import { formatWhen } from '../time';
import { UploadCancelled, uploadVideo } from '../video-upload';

/**
 * Walkthrough videos on a process (D-062): everyone who can read the process can watch them; the
 * owner (or an Admin) adds, renames and removes them while a draft is open. The player streams
 * from Blob Storage with a short-lived read link the API signs; the upload goes there directly.
 */

const mb = (bytes: number) => `${Math.max(1, Math.round(bytes / 1024 / 1024))} MB`;

export function WalkthroughVideos({ slug, canEdit }: { slug: string; canEdit: boolean }) {
  const [videos, setVideos] = useState<VideoDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(
    () =>
      api
        .listVideos(slug)
        .then((v) => {
          setVideos(v);
          setError(null);
        })
        .catch((e: unknown) => setError(describeError(e, 'The videos could not be loaded.'))),
    [slug],
  );
  useEffect(() => {
    void load();
  }, [load]);

  if (!videos && !error) return null;
  if (videos && videos.length === 0 && !canEdit) return null;

  return (
    <div className="card">
      <div className="card__header">
        <div className="card__title">{videos && videos.length === 1 ? 'Walkthrough video' : 'Walkthrough videos'}</div>
        <div className="card__meta">{canEdit ? 'MP4 or WebM, up to 500 MB each · changes while the draft is open' : 'recorded by the process owner'}</div>
      </div>
      {error && <div className="issue issue--error">{error}</div>}
      {notice && (
        <div className="issue issue--ok" role="status">
          {notice}
        </div>
      )}
      <div style={{ padding: '12px 18px 18px', display: 'grid', gap: 18 }}>
        {videos?.map((v) => <VideoItem key={v.id} slug={slug} video={v} canEdit={canEdit} onChanged={load} onNotice={setNotice} />)}
        {videos && videos.length === 0 && <div className="muted">No walkthrough video yet. A short screen recording of someone doing the process helps readers more than any description.</div>}
        {canEdit && (
          <AddVideo
            slug={slug}
            onAdded={(title) => {
              setNotice(`${title} is uploaded.`);
              void load();
            }}
          />
        )}
      </div>
    </div>
  );
}

function VideoItem({ slug, video, canEdit, onChanged, onNotice }: { slug: string; video: VideoDto; canEdit: boolean; onChanged: () => Promise<void>; onNotice: (m: string) => void }) {
  const [renaming, setRenaming] = useState(false);
  const [title, setTitle] = useState(video.title);
  const [busy, setBusy] = useState(false);
  const [playError, setPlayError] = useState(false);
  const retried = useRef(false);

  const onError = () => {
    // The read link lasts a few hours; a page left open longer needs a fresh one, once.
    if (!retried.current && new Date(video.playUrlExpiresAt).getTime() < Date.now() + 60_000) {
      retried.current = true;
      void onChanged();
      return;
    }
    setPlayError(true);
  };

  const rename = async () => {
    setBusy(true);
    try {
      await api.renameVideo(slug, video.id, title.trim());
      setRenaming(false);
      await onChanged();
    } catch (e) {
      onNotice(describeError(e, 'The video could not be renamed.'));
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!window.confirm(`Remove "${video.title}"? The recording is deleted.`)) return;
    setBusy(true);
    try {
      await api.deleteVideo(slug, video.id);
      onNotice(`${video.title} is removed.`);
      await onChanged();
    } catch (e) {
      onNotice(describeError(e, 'The video could not be removed.'));
      setBusy(false);
    }
  };

  return (
    <section aria-label={video.title}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 6 }}>
        {renaming ? (
          <div className="field" style={{ margin: 0, flex: 1 }}>
            <input aria-label={`New title for ${video.title}`} value={title} maxLength={MAX_VIDEO_TITLE} onChange={(e) => setTitle(e.target.value)} />
          </div>
        ) : (
          <strong style={{ color: 'var(--navy)' }}>{video.title}</strong>
        )}
        <span className="muted" style={{ fontSize: 12 }}>
          {video.createdByName} · {formatWhen(video.createdAt)} · {mb(video.bytes)}
        </span>
        {canEdit && (
          <span style={{ marginLeft: 'auto', whiteSpace: 'nowrap' }}>
            {renaming ? (
              <>
                <button type="button" className="button" disabled={busy || !title.trim()} onClick={() => void rename()}>
                  Save
                </button>{' '}
                <button type="button" className="button button--secondary" disabled={busy} onClick={() => { setRenaming(false); setTitle(video.title); }}>
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button type="button" className="link-button" disabled={busy} onClick={() => setRenaming(true)}>
                  Rename
                </button>{' '}
                <button type="button" className="link-button link-button--danger" disabled={busy} onClick={() => void remove()}>
                  Remove
                </button>
              </>
            )}
          </span>
        )}
      </div>
      {playError ? (
        <div className="issue issue--error">
          This browser cannot play this recording. It may use a format the browser lacks (HEVC, common on phones).{' '}
          <a href={video.playUrl} target="_blank" rel="noreferrer">
            Open the file
          </a>{' '}
          to watch it in another player.
        </div>
      ) : (
        <video controls preload="metadata" src={video.playUrl} onError={onError} style={{ width: '100%', maxHeight: 520, background: 'var(--text)', borderRadius: 'var(--radius)' }} aria-label={video.title}>
          <track kind="captions" />
        </video>
      )}
    </section>
  );
}

function AddVideo({ slug, onAdded }: { slug: string; onAdded: (title: string) => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [progress, setProgress] = useState<number | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const abort = useRef<AbortController | null>(null);
  const input = useRef<HTMLInputElement | null>(null);

  const pick = (f: File | null) => {
    setProblem(null);
    setFile(f);
    if (!f) return;
    if (!videoTypeFor(f.name, f.type)) setProblem('Videos must be MP4 or WebM. A QuickTime (.mov) recording can be exported or shared as MP4.');
    else if (f.size > MAX_VIDEO_BYTES) setProblem(`That file is ${mb(f.size)}; the limit is 500 MB. Trim it, or record at a lower resolution.`);
    if (!title.trim()) setTitle(f.name.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ').slice(0, MAX_VIDEO_TITLE));
  };

  const upload = async () => {
    if (!file) return;
    const contentType = videoTypeFor(file.name, file.type);
    if (!contentType) return;
    const t = title.trim();
    const ctrl = new AbortController();
    abort.current = ctrl;
    setProblem(null);
    setProgress(0);
    try {
      const start = await api.startVideoUpload(slug, { title: t, fileName: file.name, contentType, bytes: file.size });
      await uploadVideo(file, start.uploadUrl, contentType, start.blockBytes, setProgress, ctrl.signal);
      await api.completeVideoUpload(slug, start.id);
      setFile(null);
      setTitle('');
      if (input.current) input.current.value = '';
      onAdded(t);
    } catch (e) {
      setProblem(e instanceof UploadCancelled ? 'Upload cancelled.' : describeError(e, 'The video could not be uploaded.'));
    } finally {
      setProgress(null);
      abort.current = null;
    }
  };

  const uploading = progress !== null;
  return (
    <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14, display: 'grid', gap: 10 }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'end', flexWrap: 'wrap' }}>
        <label className="field" style={{ margin: 0 }}>
          Add a video
          <input ref={input} type="file" accept="video/mp4,video/webm,.mp4,.m4v,.webm" disabled={uploading} onChange={(e) => pick(e.target.files?.[0] ?? null)} />
        </label>
        <label className="field" style={{ margin: 0, flex: 1, minWidth: 220 }}>
          Title
          <input value={title} maxLength={MAX_VIDEO_TITLE} disabled={uploading} placeholder="For example: Full walkthrough" onChange={(e) => setTitle(e.target.value)} />
        </label>
        {uploading ? (
          <button type="button" className="button button--secondary" onClick={() => abort.current?.abort()}>
            Cancel
          </button>
        ) : (
          <button type="button" className="button" disabled={!file || !title.trim() || Boolean(problem)} onClick={() => void upload()}>
            Upload
          </button>
        )}
      </div>
      {uploading && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <progress value={progress} max={1} aria-label="Upload progress" style={{ flex: 1, accentColor: 'var(--blue)' }} />
          <span className="muted mono" style={{ fontSize: 12 }}>
            {Math.round(progress * 100)}% of {file ? mb(file.size) : ''}
          </span>
        </div>
      )}
      {problem && <div className="issue issue--error" style={{ margin: 0 }}>{problem}</div>}
    </div>
  );
}
